import type {AttributeGroup} from '@/api/hoteldetails';

export type DistanceData = {
  label: string;
  distance: string;
};

export type Distances = {
  beach?: DistanceData;
  city?: DistanceData;
  airport?: DistanceData;
  ski?: DistanceData;
};

export type DistanceType = keyof Distances;

// Maps Invia API attribute codes to our distance types
const INVIA_ATTRIBUTE_TO_DISTANCE_TYPE: Record<string, DistanceType> = {
  'IT03-DIS#BEACH': 'beach',
  'IT03-DIS#CITYC': 'city',
  'IT03-DIS#AIRP': 'airport',
  'IT03-DIS#SKIS': 'ski',
};

export const formatDistance = (distanceInMeters: number): string => {
  if (distanceInMeters < 1000) {
    return `${distanceInMeters} m`;
  }

  const kilometers = distanceInMeters / 1000;

  // For distances >= 10km, round to whole numbers
  if (kilometers >= 10) {
    return `${Math.round(kilometers)} km`;
  }

  // For distances < 10km, show one decimal place only if needed
  const rounded = Math.round(kilometers * 10) / 10;
  return rounded % 1 === 0 ? `${rounded} km` : `${rounded.toFixed(1)} km`;
};

const parseDistanceFromApiValue = (value: string): number | null => {
  if (!value?.trim()) return null;

  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const createDistanceData = (
  label: string,
  distanceInMeters: number
): DistanceData => ({
  label: label || '',
  distance: formatDistance(distanceInMeters),
});

export const extractHotelDistances = (
  attributes?: Array<AttributeGroup>
): Distances => {
  if (!attributes?.length) {
    return {};
  }

  const distances: Distances = {};

  // Flatten all attributes from all groups
  const allAttributes = attributes.flatMap((group) => group.attributes || []);

  for (const attribute of allAttributes) {
    if (!attribute.name || !attribute.value) {
      continue;
    }

    // Check if this attribute represents a distance we care about
    const distanceType = INVIA_ATTRIBUTE_TO_DISTANCE_TYPE[attribute.name];
    if (!distanceType) {
      continue;
    }

    const distanceInMeters = parseDistanceFromApiValue(attribute.value);
    if (distanceInMeters === null) {
      continue;
    }

    // Use translation if available, fallback to attribute name
    const label = attribute.translation || attribute.name;
    distances[distanceType] = createDistanceData(label, distanceInMeters);
  }

  return distances;
};

export const hasDistances = (distances: Distances): boolean => {
  return Object.keys(distances).length > 0;
};

export const getAvailableDistanceTypes = (
  distances: Distances
): Array<DistanceType> => {
  return Object.keys(distances) as Array<DistanceType>;
};

export const getDistanceByType = (
  distances: Distances,
  type: DistanceType
): DistanceData | null => {
  return distances[type] || null;
};
